import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Edit, Trash2, Users, Ban, CheckCircle, Search } from 'lucide-react';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import MemberFormDialog from './MemberFormDialog';
import { DeleteAllButton } from './DeleteAllButton';
import { API, FAMILIES_MEMBERS_PER_PAGE, paginate, getTotalPages, toggleSelectItem, toNumberOrUndefined, errDetail } from './adminShared';

const EMPTY_FORM = { family_id: '', principle_member_name: '', total_allotment: '', remaining_balance: '' };

export default function FamiliesTab({ families, members, reload, authCfg }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const [membersDialog, setMembersDialog] = useState(false);
  const [membersFamily, setMembersFamily] = useState(null);
  const [membersList, setMembersList] = useState([]);

  const [memberDialog, setMemberDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  const filtered = families
    .filter((f) => f.family_id.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (a.remaining_balance ?? 0) - (b.remaining_balance ?? 0)); // least remaining balance on top
  const paginated = paginate(filtered, page, FAMILIES_MEMBERS_PER_PAGE);

  const openEdit = (family) => {
    setEditing(family);
    setForm({ ...family, total_allotment: family?.total_allotment ?? '', remaining_balance: family?.remaining_balance ?? '' });
    setDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        const payload = {
          principle_member_name: form.principle_member_name,
          total_allotment: toNumberOrUndefined(form.total_allotment),
          remaining_balance: toNumberOrUndefined(form.remaining_balance),
        };
        Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
        await axios.put(`${API}/admin/families/${editing.family_id}`, payload, authCfg);
        toast.success('Family updated successfully');
      } else {
        const payload = {
          ...form,
          total_allotment: toNumberOrUndefined(form.total_allotment),
          remaining_balance: toNumberOrUndefined(form.remaining_balance),
        };
        await axios.post(`${API}/admin/families`, payload, authCfg);
        toast.success('Family created successfully');
      }
      setDialog(false);
      setForm(EMPTY_FORM);
      setEditing(null);
      reload();
    } catch (error) {
      toast.error(errDetail(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (familyId) => {
    setConfirm({
      title: `Delete family "${familyId}"`,
      description: 'All members of this family will also be deleted. Past claims will be retained for historical records.',
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: async () => {
        try {
          const res = await axios.delete(`${API}/admin/families/${familyId}`, authCfg);
          toast.success(res.data?.message || 'Family deleted successfully');
          reload();
        } catch (error) {
          toast.error(errDetail(error, 'Failed to delete family'));
        }
      },
    });
  };

  const handleBulkDelete = () => {
    setConfirm({
      title: `Delete ${selected.length} selected family/families`,
      description: 'All members of these families will also be deleted. Past claims will be retained for historical records.',
      confirmLabel: `Delete ${selected.length}`,
      destructive: true,
      onConfirm: async () => {
        setLoading(true);
        try {
          const res = await axios.post(`${API}/admin/families/bulk-delete`, { family_ids: selected }, authCfg);
          toast.success(res.data?.message || `Deleted ${selected.length} family/families`);
          setSelected([]);
          reload();
        } catch (error) {
          toast.error(errDetail(error, 'Bulk delete failed'));
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleBulkSuspend = (suspend) => {
    setConfirm({
      title: `${suspend ? 'Suspend' : 'Activate'} ${selected.length} family(ies)`,
      description: `${suspend ? 'Suspend' : 'Activate'} the selected families? All their members will be ${suspend ? 'suspended' : 'activated'} automatically.`,
      confirmLabel: suspend ? 'Suspend' : 'Activate',
      destructive: suspend,
      withReason: suspend,
      reasonRequired: true,
      reasonLabel: 'Reason for suspension (required)',
      reasonPlaceholder: 'e.g. Unpaid premium, fraudulent claim, member left scheme…',
      onConfirm: async (reason) => {
        if (suspend && !(reason || '').trim()) { toast.error('A reason for the suspension is required'); return; }
        setLoading(true);
        let ok = 0, fail = 0;
        for (const familyId of selected) {
          try {
            await axios.put(`${API}/admin/families/${familyId}`, suspend ? { status: 'Suspended', suspension_reason: reason.trim() } : { status: 'Active' }, authCfg);
            ok++;
          } catch {
            fail++;
          }
        }
        toast.success(`${suspend ? 'Suspended' : 'Activated'} ${ok} family(ies)${fail > 0 ? `, ${fail} failed` : ''}`);
        setSelected([]);
        reload();
        setLoading(false);
      },
    });
  };

  const handleSuspendToggle = (family) => {
    const suspend = family.status !== 'Suspended';
    setConfirm({
      title: `${suspend ? 'Suspend' : 'Unsuspend'} family "${family.family_id}"`,
      description: `All members will be automatically ${suspend ? 'suspended' : 'unsuspended'}.${suspend ? ' The reason is shown to hospitals when the family is searched.' : ''}`,
      confirmLabel: suspend ? 'Suspend' : 'Unsuspend',
      destructive: suspend,
      withReason: suspend,
      reasonRequired: true,
      reasonLabel: 'Reason for suspension (required)',
      reasonPlaceholder: 'e.g. Unpaid premium, fraudulent claim, member left scheme…',
      onConfirm: async (reason) => {
        if (suspend && !(reason || '').trim()) { toast.error('A reason for the suspension is required'); return; }
        try {
          await axios.post(`${API}/admin/families/${family.family_id}/${suspend ? 'suspend' : 'unsuspend'}`, { reason: (reason || '').trim() }, authCfg);
          toast.success(`Family ${suspend ? 'suspended' : 'unsuspended'} successfully`);
          reload();
        } catch (error) {
          toast.error(errDetail(error, `Failed to ${suspend ? 'suspend' : 'unsuspend'} family`));
        }
      },
    });
  };

  const openMembersDialog = (family) => {
    setMembersFamily(family);
    setMembersList(members.filter((m) => m.family_id === family.family_id).sort((a, b) => a.serial_number.localeCompare(b.serial_number, undefined, { numeric: true }))); // principal (-00) first, then -01 … -07
    setMembersDialog(true);
  };

  const handleMemberSuspendToggle = (member) => {
    const suspend = member.status !== 'Suspended';
    setConfirm({
      title: `${suspend ? 'Suspend' : 'Unsuspend'} member "${member.serial_number}"`,
      description: suspend ? 'The member will be blocked from new visits and claims until reactivated. The reason is shown to hospitals when the member is searched.' : 'The member will be eligible for visits and claims again.',
      confirmLabel: suspend ? 'Suspend' : 'Unsuspend',
      destructive: suspend,
      withReason: suspend,
      reasonRequired: true,
      reasonLabel: 'Reason for suspension (required)',
      reasonPlaceholder: 'e.g. Unpaid premium, fraudulent claim, member left scheme…',
      onConfirm: async (reason) => {
        if (suspend && !(reason || '').trim()) { toast.error('A reason for the suspension is required'); return; }
        try {
          await axios.post(`${API}/admin/members/${member.serial_number}/${suspend ? 'suspend' : 'unsuspend'}`, { reason: (reason || '').trim() }, authCfg);
          toast.success(`Member ${suspend ? 'suspended' : 'activated'}`);
          setMembersList(membersList.map((m) => (m.serial_number === member.serial_number ? { ...m, status: suspend ? 'Suspended' : 'Active', suspension_reason: suspend ? reason.trim() : null } : m)));
          reload();
        } catch (error) {
          toast.error(errDetail(error, 'Failed to update member status'));
        }
      },
    });
  };

  const handleMemberDelete = (serialNumber) => {
    setConfirm({
      title: 'Delete this member',
      description: 'Their past claims will be retained for historical records.',
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/admin/members/${serialNumber}`, authCfg);
          toast.success('Member deleted');
          setMembersList(membersList.filter((m) => m.serial_number !== serialNumber));
          reload();
        } catch (error) {
          toast.error('Failed to delete member');
        }
      },
    });
  };

  const handleMemberEdit = (member) => {
    setEditingMember(member);
    setMembersDialog(false);
    setTimeout(() => setMemberDialog(true), 350);
  };

  return (
    <Card>
      <ConfirmDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} {...(confirm || {})} />
      <MemberFormDialog open={memberDialog} onOpenChange={setMemberDialog} member={editingMember} families={families} authCfg={authCfg} onSaved={reload} />
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Families</CardTitle>
        <div className="flex gap-2">
          <DeleteAllButton
            label="Delete All Families"
            title="Delete ALL families & members"
            description={'This permanently removes EVERY family and EVERY member from the system.\nClaims history is kept but will reference deleted families.\nThis cannot be undone.'}
            url={`${API}/admin/families/all`}
            onDone={reload}
            testId="delete-all-families"
          />
          {selected.length > 0 && (
            <>
              <Button variant="outline" size="sm" onClick={() => handleBulkSuspend(false)} className="border-green-500 text-green-700 hover:bg-green-50">
                <CheckCircle className="w-4 h-4 mr-2" />
                Activate {selected.length} Selected
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleBulkSuspend(true)} className="border-orange-500 text-orange-700 hover:bg-orange-50">
                <Ban className="w-4 h-4 mr-2" />
                Suspend {selected.length} Selected
              </Button>
              <Button variant="destructive" size="sm" onClick={handleBulkDelete} data-testid="bulk-delete-families-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete {selected.length} Selected
              </Button>
            </>
          )}
          <Dialog open={dialog} onOpenChange={setDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editing ? 'Edit Family' : 'Add Family'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Family ID</Label>
                  <Input value={form.family_id} onChange={(e) => setForm({ ...form, family_id: e.target.value })} required disabled={!!editing} />
                </div>
                <div>
                  <Label>Principal Member Name</Label>
                  <Input value={form.principle_member_name} onChange={(e) => setForm({ ...form, principle_member_name: e.target.value })} required />
                </div>
                <div>
                  <Label>Total Allotment ($)</Label>
                  <Input type="number" step="0.01" value={form.total_allotment} onChange={(e) => setForm({ ...form, total_allotment: e.target.value })} required />
                </div>
                <div>
                  <Label>Remaining Balance ($)</Label>
                  <Input type="number" step="0.01" value={form.remaining_balance} onChange={(e) => setForm({ ...form, remaining_balance: e.target.value })} required />
                </div>
                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? 'Saving...' : editing ? 'Update' : 'Create'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input placeholder="Search by Family ID (e.g., SEC-2413)..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="family-search-input" />
          </div>
          {search && <p className="text-sm text-gray-500 mt-2">Found {filtered.length} family/families</p>}
        </div>
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="text-center p-3 text-sm font-semibold w-12">
                <Checkbox
                  checked={paginated.length > 0 && paginated.every((f) => selected.includes(f.family_id))}
                  onCheckedChange={() => {
                    const pageIds = paginated.map((f) => f.family_id);
                    const allSelected = paginated.length > 0 && paginated.every((f) => selected.includes(f.family_id));
                    if (allSelected) {
                      setSelected(selected.filter((id) => !pageIds.includes(id)));
                    } else {
                      setSelected([...new Set([...selected, ...pageIds])]);
                    }
                  }}
                  data-testid="select-all-families-checkbox"
                />
              </th>
              <th className="text-left p-3 text-sm font-semibold">Family ID</th>
              <th className="text-left p-3 text-sm font-semibold">Principal Member</th>
              <th className="text-right p-3 text-sm font-semibold">Total Allotment</th>
              <th className="text-right p-3 text-sm font-semibold">Remaining Balance</th>
              <th className="text-center p-3 text-sm font-semibold">Status</th>
              <th className="text-center p-3 text-sm font-semibold">Members</th>
              <th className="text-center p-3 text-sm font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((family) => (
              <tr key={family.family_id} className="border-b hover:bg-gray-50">
                <td className="p-3 text-center">
                  <Checkbox
                    checked={selected.includes(family.family_id)}
                    onCheckedChange={() => toggleSelectItem(family.family_id, selected, setSelected)}
                    data-testid={`select-family-${family.family_id}`}
                  />
                </td>
                <td className="p-3 text-sm font-medium">{family.family_id}</td>
                <td className="p-3 text-sm">{family.principle_member_name || <span className="text-gray-400 italic">Not set</span>}</td>
                <td className="p-3 text-sm text-right">${family.total_allotment.toFixed(2)}</td>
                <td className="p-3 text-sm text-right font-medium">$ {family.remaining_balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td className="p-3 text-center">
                  <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                    family.status === 'Suspended' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {family.status || 'Active'}
                  </span>
                  {family.status === 'Suspended' && family.suspension_reason && (
                    <div className="mt-1 text-xs text-red-700 max-w-[220px] mx-auto" data-testid={`family-suspension-reason-${family.family_id}`}>{family.suspension_reason}</div>
                  )}
                </td>
                <td className="p-3 text-center">
                  <Button variant="outline" size="sm" onClick={() => openMembersDialog(family)} className="text-blue-600 border-blue-300 hover:bg-blue-50" data-testid={`view-members-${family.family_id}`}>
                    <Users className="w-4 h-4 mr-1" />
                    View
                  </Button>
                </td>
                <td className="p-3 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(family)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    {family.status === 'Suspended' ? (
                      <Button variant="ghost" size="sm" onClick={() => handleSuspendToggle(family)} className="text-green-600" title="Unsuspend Family">
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" onClick={() => handleSuspendToggle(family)} className="text-orange-600" title="Suspend Family">
                        <Ban className="w-4 h-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(family.family_id)} className="text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length > FAMILIES_MEMBERS_PER_PAGE && (
          <Pagination currentPage={page} totalPages={getTotalPages(filtered.length, FAMILIES_MEMBERS_PER_PAGE)} onPageChange={setPage} />
        )}
      </CardContent>

      <Dialog open={membersDialog} onOpenChange={setMembersDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Family Members - {membersFamily?.family_id}
            </DialogTitle>
            {membersFamily && (
              <p className="text-sm text-gray-500">
                Principal: {membersFamily.principle_member_name || <span className="italic">Not set</span>}
              </p>
            )}
          </DialogHeader>
          {membersList.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No members found for this family</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-sm text-gray-600">{membersList.length} member(s) in this family</div>
              <table className="w-full border rounded-lg">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left p-3 text-sm font-semibold">Serial Number</th>
                    <th className="text-left p-3 text-sm font-semibold">Name</th>
                    <th className="text-left p-3 text-sm font-semibold">Relationship</th>
                    <th className="text-left p-3 text-sm font-semibold">DOB</th>
                    <th className="text-center p-3 text-sm font-semibold">Status</th>
                    <th className="text-center p-3 text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {membersList.map((member) => (
                    <tr key={member.serial_number} className="border-b hover:bg-gray-50">
                      <td className="p-3 text-sm font-medium">{member.serial_number}</td>
                      <td className="p-3 text-sm">{member.first_name} {member.middle_name} {member.last_name}</td>
                      <td className="p-3 text-sm">
                        <span className={`inline-flex px-2 py-1 text-xs rounded ${
                          member.relationship === 'Principle' ? 'bg-blue-100 text-blue-800 font-semibold' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {member.relationship === 'Principle' ? 'Principal' : member.relationship}
                        </span>
                      </td>
                      <td className="p-3 text-sm text-gray-600">{member.dob}</td>
                      <td className="p-3 text-center">
                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                          member.status === 'Suspended' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {member.status || 'Active'}
                        </span>
                        {member.status === 'Suspended' && member.suspension_reason && (
                          <div className="mt-1 text-xs text-red-700 max-w-[200px] mx-auto" data-testid={`member-suspension-reason-${member.serial_number}`}>{member.suspension_reason}</div>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleMemberEdit(member)} title="Edit Member">
                            <Edit className="w-4 h-4" />
                          </Button>
                          {member.status === 'Suspended' ? (
                            <Button variant="ghost" size="sm" onClick={() => handleMemberSuspendToggle(member)} className="text-green-600" title="Activate Member">
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button variant="ghost" size="sm" onClick={() => handleMemberSuspendToggle(member)} className="text-orange-600" title="Suspend Member">
                              <Ban className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleMemberDelete(member.serial_number)} className="text-red-600" title="Delete Member">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setMembersDialog(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
